import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { LogOut, User, Menu, X, UserRound, CircleUserRound } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import NotificationBell from './NotificationBell';

export default function Navbar({ customRightAction, isStatic }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const onProviders = location.pathname.startsWith('/proveedores');
  // Páginas con hero a pantalla completa donde la barra flota SOBRE la imagen (no reservar espacio)
  const heroOverlap = location.pathname === '/' || onProviders;

  // Auto-ocultar la barra al hacer scroll hacia abajo; mostrarla al subir
  useEffect(() => {
    if (isStatic) return;
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > last && y > 120 && !menuOpen);
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isStatic, menuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  const getDashboardPath = () => {
    if (!user) return '/';
    if (user.role === 'admin') return '/admin/dashboard';
    if (user.role === 'provider') return '/provider/dashboard';
    return '/client/dashboard';
  };

  const handleNav = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  return (
    <>
      {/* Espaciador: reserva la altura de la barra flotante en páginas internas (evita que tape el contenido) */}
      {!isStatic && !heroOverlap && <div aria-hidden="true" className="h-28 md:h-32" />}
      <nav className={`${isStatic ? '' : `fixed top-5 left-1/2 -translate-x-1/2 z-50 transition-transform duration-300 ${hidden ? '-translate-y-[200%]' : ''}`} w-[95%] max-w-7xl rounded-3xl bg-white/70 backdrop-blur-xl border border-white/40 shadow-xl`}>
        <div className="mx-auto px-10 max-w-7xl">
          <div className="flex items-center justify-between py-2">

            {/* Brand — siempre lleva a inicio (público) o al panel según el rol */}
            <button
              type="button"
              onClick={() => navigate('/')}
              aria-label={t('navbar.goHome', 'Ir a inicio')}
              className="relative z-10 cursor-pointer shrink-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#134168] active:opacity-80 transition-opacity"
            >
              <img src={`${process.env.PUBLIC_URL}/images/logo.png`} alt="RedMobility" className="h-20 md:h-24 max-w-none object-contain pointer-events-none" />
            </button>

            {/* Desktop right side */}
            <div className="hidden md:flex items-center gap-4">
            <button
  onClick={() => navigate(onProviders ? '/' : '/proveedores')}
  className="w-[220px] py-2 rounded-full font-semibold text-lg text-[#0B2545] hover:bg-[#E6F1FB] transition-colors flex items-center justify-center gap-2"
>
  <UserRound className="w-5 h-5" />
  {onProviders ? t('navbar.forClients', 'Para clientes') : t('navbar.providers')}
</button>
              <LanguageSwitcher />
              {customRightAction && customRightAction}

              {!customRightAction && !user && (
                <>
                 <button
  onClick={() => navigate('/login')}
  className="px-10 py-2 rounded-full font-semibold text-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
>
  <CircleUserRound className="w-5 h-5" />
  {t('navbar.signIn')}
</button>
                  <button
                    onClick={() => navigate('/register')}
                    className="bg-gradient-to-r from-[#36C0C7] to-[#134168] text-white px-8 py-3 rounded-3xl font-bold text-lg shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    {onProviders ? t('navbar.publishService', 'Publicar mi servicio') : t('navbar.signUp')}
                  </button>
                </>
              )}

              {!customRightAction && user && (
                <div className="flex items-center gap-4">
                  <NotificationBell />
                  <button
                    onClick={() => navigate(getDashboardPath())}
                    className="flex items-center gap-2 hover:bg-gray-100 px-4 py-2 rounded-full transition-colors"
                  >
                    <div className="w-8 h-8 bg-[#134168] rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {user.name?.[0] || <User className="w-4 h-4" />}
                    </div>
                    <span className="font-medium">{user.name}</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
                    title={t('navbar.logoutTitle')}
                    aria-label={t('navbar.logoutTitle')}
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>

            {/* Mobile right side */}
            <div className="flex md:hidden items-center gap-2">
              <LanguageSwitcher />
              {user && <NotificationBell />}
              {/* If user is logged in: show avatar + hamburger */}
              {user && (
                <div className="w-8 h-8 bg-[#134168] rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0">
                  {user.name?.[0] || <User className="w-4 h-4" />}
                </div>
              )}
              {/* Hamburger button */}
              <button
                id="mobile-menu-toggle"
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 rounded-full hover:bg-gray-100 active:opacity-75 transition-colors"
                aria-label={menuOpen ? t('common.closeMenu') : t('common.openMenu')}
                aria-expanded={menuOpen}
                aria-controls="mobile-menu"
              >
                {menuOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile dropdown menu — menú secundario.
            La navegación principal (Inicio, Explorar, Ingresar, Registro) vive en la
            barra inferior (BottomNav); aquí solo dejamos lo que esa barra NO ofrece,
            para evitar botones duplicados. */}
        {menuOpen && (
          <div id="mobile-menu" className="md:hidden border-t border-gray-100 bg-white shadow-lg animate-slideDown" role="navigation" aria-label={t('navbar.ariaMainNav')}>
            <div className="container mx-auto px-4 py-4 flex flex-col gap-2">

              {/* Logged in: cuenta */}
              {user && (
                <>
                  <div className="px-4 py-2 border-b border-gray-100 mb-1">
                    <p className="font-bold text-gray-900">{user.name}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <button
                    onClick={() => handleNav(getDashboardPath())}
                    className="w-full text-left px-4 py-3 rounded-xl font-medium text-gray-700 hover:bg-gray-50 active:opacity-75 transition-colors flex items-center gap-3 min-h-[48px]"
                  >
                    <User className="w-5 h-5 text-gray-400" />
                    {t('navbar.myProfile')}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 rounded-xl font-medium text-red-600 hover:bg-red-50 active:opacity-75 transition-colors flex items-center gap-3 min-h-[48px]"
                  >
                    <LogOut className="w-5 h-5" />
                    {t('navbar.logout')}
                  </button>
                </>
              )}

              {/* Cambiar de audiencia (no está en la barra inferior) */}
              <div className={`flex flex-col gap-1 ${user ? 'border-t border-gray-100 mt-2 pt-2' : ''}`}>
                <button
                  onClick={() => handleNav(onProviders ? '/' : '/proveedores')}
                  className="w-full text-left px-4 py-3 rounded-xl font-semibold text-[#0B2545] hover:bg-[#E6F1FB] active:opacity-75 transition-colors min-h-[48px]"
                >
                  💼 {onProviders ? t('navbar.forClients', 'Para clientes') : t('navbar.providers')}
                </button>
                {!user && onProviders && (
                  <button
                    id="mobile-nav-publish"
                    onClick={() => handleNav('/register')}
                    className="w-full bg-[#134168] text-white px-4 py-3 rounded-xl font-bold hover:bg-[#39b4aa] active:opacity-75 transition-colors min-h-[48px]"
                  >
                    {t('navbar.publishService', 'Publicar mi servicio')}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Backdrop to close menu on outside click */}
      {menuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/20"
          onClick={() => setMenuOpen(false)}
        />
      )}
    </>
  );
}
